import { useAppContext } from "@contexts/AppContext";
import { useAuthContext } from "@contexts/AuthContext";
import { useStorageContext } from "@contexts/StorageContext";
import { useWhoAmI } from "@repo/components/ctrl";
import type { PostHog } from "posthog-js";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type PropsWithChildren,
} from "react";
import { resolveAnalyticsConfig, type AnalyticsConfig } from "./config";
import { CONSENT_CHANGED_EVENT } from "../monitoring/consent";
import { readConsent, writeConsent, type Consent } from "./consent";
import type { AnalyticsEvent } from "./events";
import { scrubUrls } from "./sanitize";

export type EventProps = Record<string, unknown>;

export interface TrackOptions {
  /**
   * Bypass the batch queue and send immediately over sendBeacon. Use for
   * events fired right before a full-page navigation, which would otherwise
   * drop the queue or abort an in-flight fetch.
   */
  flush?: boolean;
}

export interface AnalyticsContextValue {
  /** True when analytics is configured, consented to, and running. */
  enabled: boolean;
  /**
   * True when PostHog or Faro browser monitoring is configured; both are
   * gated by the banner, which is moot otherwise.
   */
  configured: boolean;
  /** True when PostHog is configured (NA_POSTHOG_KEY). */
  analytics: boolean;
  /** True when Faro browser monitoring is configured (NA_FARO_URL). */
  monitoring: boolean;
  /** The stored banner choice; null until the user chooses. */
  consent: Consent | null;
  setConsent: (consent: Consent) => void;
  /** Whether the consent banner is showing (first visit or reopened). */
  bannerOpen: boolean;
  /** Reopen the banner so the choice can be changed. */
  openPreferences: () => void;
  track: (name: AnalyticsEvent, props?: EventProps, opts?: TrackOptions) => void;
}

const noop = () => {};

const AnalyticsContext = createContext<AnalyticsContextValue>({
  enabled: false,
  configured: false,
  analytics: false,
  monitoring: false,
  consent: null,
  setConsent: noop,
  bannerOpen: false,
  openPreferences: noop,
  track: noop,
});

export function useAnalytics(): AnalyticsContextValue {
  return useContext(AnalyticsContext);
}

let initializedFor: string | null = null;

/**
 * Load posthog-js on demand so its code is not even fetched or executed
 * before the user consents.
 */
async function loadPosthog(): Promise<PostHog> {
  const mod = await import("posthog-js");
  return mod.posthog;
}

/**
 * Init posthog-js (idempotent per key/host): in-memory persistence, explicit
 * events plus heatmaps (click/scroll positions, no element text), no
 * replay/autocapture. Heatmaps and performance capture are set here so the
 * PostHog project settings cannot widen collection beyond the consent text.
 * Must only be called after consent.
 */
function ensureInit(posthog: PostHog, cfg: AnalyticsConfig): boolean {
  const id = `${cfg.host}|${cfg.key}`;
  if (initializedFor === id) return true;
  try {
    posthog.init(cfg.key, {
      api_host: cfg.host,
      persistence: "memory",
      // Memory persistence mints a new anonymous id per page load; without
      // this, every identify() merges another one into the person until it
      // hits PostHog's distinct-id limit. Pre-login events stay anonymous.
      reuseAnonymousId: true,
      autocapture: false,
      capture_pageview: false,
      // Scroll depth rides on the next $pageview or $pageleave; without
      // pageleave the last page of a visit never reports it.
      capture_pageleave: true,
      // The shell scrolls <main>, not the window (shell.module.scss .main).
      scroll_root_selector: "main",
      disable_session_recording: true,
      enable_heatmaps: true,
      capture_performance: false,
      disable_surveys: true,
      advanced_disable_flags: true,
      person_profiles: "identified_only",
      before_send: scrubUrls,
    });
    initializedFor = id;
    return true;
  } catch {
    // analytics must never break the app
    return false;
  }
}

/** Test hook: forget the init guard so each test starts from a clean slate. */
export function resetAnalyticsInitForTests() {
  initializedFor = null;
}

/**
 * Product analytics via posthog-js. Opt-in: nothing is loaded, initialised or
 * sent until the banner choice is "accepted". Inert when NA_POSTHOG_KEY is
 * unset, and again (opted out and reset) if consent is later withdrawn.
 */
export const PostHogProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    env: { NA_POSTHOG_HOST, NA_POSTHOG_KEY, NA_POSTHOG_PROPERTIES, NA_FARO_URL },
  } = useAppContext();
  const { storage } = useStorageContext();
  const { isAuthenticated } = useAuthContext();
  const authenticated = isAuthenticated();

  const config = useMemo(
    () =>
      resolveAnalyticsConfig({
        NA_POSTHOG_HOST,
        NA_POSTHOG_KEY,
        NA_POSTHOG_PROPERTIES,
      }),
    [NA_POSTHOG_HOST, NA_POSTHOG_KEY, NA_POSTHOG_PROPERTIES],
  );

  const [consent, setConsentState] = useState<Consent | null>(() =>
    readConsent(storage),
  );

  // Follow answers given in other tabs (storage) or by other writers in this
  // tab, so withdrawing consent anywhere stops capture here too.
  useEffect(() => {
    const sync = () => setConsentState(readConsent(storage));
    window.addEventListener(CONSENT_CHANGED_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CONSENT_CHANGED_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [storage]);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  // The user_id currently identified in posthog; null when anonymous.
  const identifiedRef = useRef<string | null>(null);
  // Set once posthog-js has been loaded and initialised after consent.
  const phRef = useRef<PostHog | null>(null);
  // readyRef gates track() synchronously; ready state drives re-renders.
  const readyRef = useRef(false);
  const [ready, setReady] = useState(false);

  // Consent drives the whole lifecycle: load+init on accept, opt out and
  // reset on withdrawal. Nothing runs while consent is null or declined.
  useEffect(() => {
    if (!config || consent !== "accepted") {
      const ph = phRef.current;
      readyRef.current = false;
      identifiedRef.current = null;
      setReady(false);
      if (!ph) return;
      try {
        ph.opt_out_capturing();
        ph.reset();
      } catch {
        // analytics must never break the app
      }
      return;
    }
    let cancelled = false;
    loadPosthog()
      .then((ph) => {
        if (cancelled || !ensureInit(ph, config)) return;
        if (ph.has_opted_out_capturing()) {
          ph.opt_in_capturing({ captureEventName: false });
        }
        // reset() on withdrawal also cleared super properties
        ph.register({ ...config.properties, source: "portal" });
        phRef.current = ph;
        readyRef.current = true;
        setReady(true);
      })
      .catch(() => {
        // script failed to load (blocker, offline): stay inert
      });
    return () => {
      cancelled = true;
    };
  }, [config, consent]);

  const enabled = ready && consent === "accepted";

  // identify by ctrl user id once whoami resolves (never the email)
  const { data: whoami } = useWhoAmI({
    isPaused: () => !ready || !authenticated,
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });
  const userId = whoami?.user_id;
  const tenantId = whoami?.tenant_id ?? undefined;

  useEffect(() => {
    const posthog = phRef.current;
    if (!ready || !posthog || !userId) return;
    // account switch without a logout: drop the previous identity first so
    // the new user is not merged into it
    if (identifiedRef.current && identifiedRef.current !== userId) {
      posthog.unregister("tenant_id");
      posthog.reset();
      posthog.register({ ...config?.properties, source: "portal" });
    }
    if (tenantId) posthog.register({ tenant_id: tenantId });
    posthog.identify(userId, tenantId ? { tenant_id: tenantId } : undefined);
    identifiedRef.current = userId;
  }, [ready, userId, tenantId, config]);

  // logout: drop identity so the next user is not merged into this one
  useEffect(() => {
    const posthog = phRef.current;
    if (!ready || !posthog || authenticated || !identifiedRef.current) return;
    identifiedRef.current = null;
    posthog.unregister("tenant_id");
    posthog.reset();
    posthog.register({ ...config?.properties, source: "portal" });
  }, [ready, authenticated, config]);

  const setConsent = useCallback(
    (choice: Consent) => {
      writeConsent(storage, choice);
      setConsentState(choice);
      setPreferencesOpen(false);
    },
    [storage],
  );
  const openPreferences = useCallback(() => setPreferencesOpen(true), []);

  const track = useCallback<AnalyticsContextValue["track"]>(
    (name, props, opts) => {
      // Before consent (or after withdrawal) events are dropped, not queued.
      const posthog = phRef.current;
      if (!readyRef.current || !posthog) return;
      try {
        posthog.capture(
          name,
          props,
          opts?.flush
            ? { send_instantly: true, transport: "sendBeacon" }
            : undefined,
        );
      } catch {
        // analytics must never break the UI
      }
    },
    [],
  );

  const analytics = config !== null;
  const monitoring = Boolean(NA_FARO_URL?.trim());
  const configured = analytics || monitoring;
  const bannerOpen = configured && (consent === null || preferencesOpen);

  const value = useMemo(
    () => ({
      enabled,
      configured,
      analytics,
      monitoring,
      consent,
      setConsent,
      bannerOpen,
      openPreferences,
      track,
    }),
    [
      enabled,
      configured,
      analytics,
      monitoring,
      consent,
      setConsent,
      bannerOpen,
      openPreferences,
      track,
    ],
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};
